import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from './cn.js';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'outline' | 'danger' | 'subtle';
export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg';

/**
 * The variants share one geometry and differ only in surface treatment, so a
 * row of mixed buttons still lines up on the same optical baseline.
 *
 * `active:translate-y-px` is the whole trick behind buttons that feel real:
 * a one-pixel press, fast, on every variant.
 */
const VARIANT: Record<ButtonVariant, string> = {
  primary: cn(
    'bg-brand text-ink-on-brand shadow-xs',
    // A top inner highlight gives the fill a subtle curvature rather than
    // reading as a flat rectangle.
    'shadow-[inset_0_1px_0_rgb(255_255_255/0.14),var(--shadow-xs)]',
    'hover:bg-brand-hover active:bg-brand-active',
  ),
  secondary: cn(
    'bg-surface text-ink shadow-xs ring-1 ring-inset ring-line-strong/70',
    'hover:bg-surface-hover active:bg-surface-active',
  ),
  outline: cn(
    'bg-transparent text-ink ring-1 ring-inset ring-line-strong',
    'hover:bg-surface-hover active:bg-surface-active',
  ),
  ghost: 'bg-transparent text-ink-secondary hover:bg-surface-hover hover:text-ink active:bg-surface-active',
  subtle: 'bg-surface-sunken text-ink-secondary hover:bg-surface-hover hover:text-ink',
  danger: cn(
    'bg-danger text-white shadow-xs',
    'shadow-[inset_0_1px_0_rgb(255_255_255/0.16),var(--shadow-xs)]',
    'hover:brightness-110 active:brightness-95',
  ),
};

const SIZE: Record<ButtonSize, string> = {
  xs: 'h-6 gap-1 rounded-xs px-1.5 text-2xs',
  sm: 'h-7 gap-1.5 rounded-sm px-2.5 text-xs',
  md: 'h-8 gap-1.5 rounded-md px-3 text-sm',
  lg: 'h-9 gap-2 rounded-md px-3.5 text-base',
};

const ICON_ONLY: Record<ButtonSize, string> = {
  xs: 'w-6 px-0',
  sm: 'w-7 px-0',
  md: 'w-8 px-0',
  lg: 'w-9 px-0',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Renders a spinner and blocks interaction without changing the width. */
  loading?: boolean;
  iconOnly?: boolean;
  leading?: ReactNode;
  trailing?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'secondary',
    size = 'md',
    loading = false,
    iconOnly = false,
    leading,
    trailing,
    className,
    children,
    disabled,
    type = 'button',
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled ?? loading}
      aria-busy={loading || undefined}
      className={cn(
        'relative inline-flex select-none items-center justify-center whitespace-nowrap font-medium',
        'transition-[background-color,box-shadow,color,transform] duration-fast',
        'active:translate-y-px',
        'disabled:pointer-events-none disabled:opacity-45',
        SIZE[size],
        iconOnly && ICON_ONLY[size],
        VARIANT[variant],
        className,
      )}
      {...rest}
    >
      {loading && (
        <Loader2
          aria-hidden
          className={cn('animate-spin', size === 'xs' || size === 'sm' ? 'h-3 w-3' : 'h-3.5 w-3.5')}
        />
      )}
      {!loading && leading}
      {children}
      {!loading && trailing}
    </button>
  );
});
